import { MONITORING_SECTIONS } from "./monitoringConstants";

export const LOCAL_SETUP_STORAGE_KEY = "rcc-assessment-setups";
export const LOCAL_MONITORING_SUBMISSIONS_STORAGE_KEY = "rcc-performance-monitoring-submissions";

const DEFAULT_SUMMARY_FIELDS = {
  strength: "",
  weakness: "",
  technicalBackstopping: "",
};

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function normalizeCode(value) {
  return String(value || "").trim().toLowerCase();
}

function toSafeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getFallbackSectionMetadataMaps() {
  const sectionsByTitle = new Map();
  const indicatorsByTitle = new Map();

  MONITORING_SECTIONS.forEach((section) => {
    sectionsByTitle.set(normalizeText(section.title), section);
    sectionsByTitle.set(normalizeText(section.code), section);

    section.indicators.forEach((indicator) => {
      indicatorsByTitle.set(normalizeText(indicator.title), indicator);
      indicatorsByTitle.set(normalizeText(indicator.code), indicator);
    });
  });

  return { sectionsByTitle, indicatorsByTitle };
}

function getSectionIcon(title) {
  const normalizedTitle = normalizeText(title);

  if (normalizedTitle.includes("planning")) return "Network";
  if (normalizedTitle.includes("procurement")) return "ClipboardList";
  if (normalizedTitle.includes("environment") || normalizedTitle.includes("sanitation")) return "Trash2";
  if (normalizedTitle.includes("management") || normalizedTitle.includes("administration")) return "Building2";
  if (normalizedTitle.includes("human resource") || normalizedTitle.includes("staff")) return "GraduationCap";
  if (normalizedTitle.includes("financial") || normalizedTitle.includes("audit")) return "Landmark";
  return "Layers3";
}

function sortByCode(left, right, leftFallback, rightFallback) {
  return String(left || leftFallback || "").localeCompare(String(right || rightFallback || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export function readStoredJson(key, fallbackValue) {
  if (typeof window === "undefined") {
    return fallbackValue;
  }

  try {
    const rawValue = window.localStorage.getItem(key);
    if (!rawValue) {
      return fallbackValue;
    }

    return JSON.parse(rawValue);
  } catch (error) {
    console.warn(`Unable to read local storage key "${key}":`, error);
    return fallbackValue;
  }
}

export function writeStoredJson(key, value) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Unable to write local storage key "${key}":`, error);
  }
}

export function normalizeBackendRows(response) {
  const payload = response?.data;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

export function getRowsForPeriod(rows, regionCode, year, quarter) {
  const items = Array.isArray(rows) ? rows : [];

  return items.filter(
    (item) =>
      String(item?.regionCode || "").toLowerCase() === String(regionCode || "").toLowerCase() &&
      String(item?.year || "") === String(year || "") &&
      String(item?.quarter || "") === String(quarter || ""),
  );
}

export function getIndicatorIdsFromRows(rows) {
  const items = Array.isArray(rows) ? rows : [];
  const ids = new Set();

  items.forEach((item) => {
    const directIndicatorId = toSafeNumber(item?.indicatorId || item?.indicator?.indicatorId);
    if (directIndicatorId) {
      ids.add(directIndicatorId);
    }

    if (!Array.isArray(item?.indicators)) {
      return;
    }

    item.indicators.forEach((value) => {
      const parsed = toSafeNumber(value);
      if (parsed) {
        ids.add(parsed);
      }
    });
  });

  return Array.from(ids);
}

export function getRegionCodeForLocation(locationNode) {
  if (!locationNode) return "";
  if (locationNode.level === "region") return locationNode.orgUnitId || locationNode.id || locationNode.slug || "";
  if (locationNode.level === "district") {
    return locationNode.regionOrgUnitId || locationNode.parentId || `region:${locationNode.regionSlug || ""}`;
  }
  if (locationNode.level === "subdistrict") {
    return locationNode.parentRegionOrgUnitId || locationNode.parentRegionId || `region:${locationNode.regionSlug || ""}`;
  }
  return "";
}

export function getLegacyRegionCodesForLocation(locationNode) {
  if (!locationNode) {
    return [];
  }

  const legacyCodes = new Set();

  if (locationNode.level === "region") {
    if (locationNode.id) {
      legacyCodes.add(String(locationNode.id));
    }

    if (locationNode.slug) {
      legacyCodes.add(`region:${locationNode.slug}`);
    }
  }

  if (locationNode.level === "district") {
    if (locationNode.parentId) {
      legacyCodes.add(String(locationNode.parentId));
    }

    if (locationNode.regionSlug) {
      legacyCodes.add(`region:${locationNode.regionSlug}`);
    }
  }

  if (locationNode.level === "subdistrict") {
    if (locationNode.parentRegionId) {
      legacyCodes.add(String(locationNode.parentRegionId));
    }

    if (locationNode.regionSlug) {
      legacyCodes.add(`region:${locationNode.regionSlug}`);
    }
  }

  return Array.from(legacyCodes).filter(Boolean);
}

export function buildAssessmentPeriodKey({ regionCode, year, quarter }) {
  return [regionCode || "region", year || "year", quarter || "quarter"].join("::");
}

export function buildDistrictSubmissionKey({ regionCode, districtId, year, quarter }) {
  return [buildAssessmentPeriodKey({ regionCode, year, quarter }), districtId || "district"].join("::");
}

export function createInitialDraftValues(sections, existingValues = {}) {
  return (Array.isArray(sections) ? sections : []).reduce((acc, section) => {
    (section.indicators || []).forEach((indicator) => {
      acc[indicator.key] = {
        findings: "",
        remarks: "",
        points: "",
        ...(existingValues[indicator.key] || {}),
      };
    });

    return acc;
  }, {});
}

export function createInitialAssessmentSummary(existingSummary = {}) {
  return {
    ...DEFAULT_SUMMARY_FIELDS,
    ...(existingSummary || {}),
  };
}

export function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function clampPointsValue(value, maxScore) {
  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return "";
  }

  return String(Math.min(Math.max(numericValue, 0), maxScore));
}

export function getDraftIndicatorStatus(values) {
  const hasFindings = Boolean(stripHtml(values?.findings));
  const hasRemarks = Boolean(stripHtml(values?.remarks));
  const hasPoints = String(values?.points || "").trim() !== "";

  if (!hasFindings && !hasRemarks && !hasPoints) {
    return "Not Started";
  }

  if (hasFindings && hasPoints) {
    return "Complete";
  }

  return "In Progress";
}

export function getStatusProgressValue(status) {
  if (status === "Complete") return 100;
  if (status === "In Progress") return 60;
  return 0;
}

export function getSavedMonitoringSubmissions() {
  return readStoredJson(LOCAL_MONITORING_SUBMISSIONS_STORAGE_KEY, []);
}

export function getStoredAssessmentSetupsForLocation(locationNode) {
  const rows = readStoredJson(LOCAL_SETUP_STORAGE_KEY, []);
  const regionCode = getRegionCodeForLocation(locationNode);
  const legacyRegionCodes = getLegacyRegionCodesForLocation(locationNode);

  if (!regionCode || legacyRegionCodes.length === 0) {
    return rows;
  }

  const normalizedRegionCode = normalizeCode(regionCode);
  const legacyRegionCodeSet = new Set(legacyRegionCodes.map((value) => normalizeCode(value)));
  let didChange = false;

  const nextRows = rows.map((row) => {
    const rowRegionCode = normalizeCode(row?.regionCode);

    if (!legacyRegionCodeSet.has(rowRegionCode) || rowRegionCode === normalizedRegionCode) {
      return row;
    }

    didChange = true;
    return {
      ...row,
      regionCode,
    };
  });

  if (didChange) {
    writeStoredJson(LOCAL_SETUP_STORAGE_KEY, nextRows);
  }

  return nextRows;
}

export function getSavedMonitoringSubmissionsForLocation(locationNode) {
  const rows = readStoredJson(LOCAL_MONITORING_SUBMISSIONS_STORAGE_KEY, []);
  const regionCode = getRegionCodeForLocation(locationNode);
  const legacyRegionCodes = getLegacyRegionCodesForLocation(locationNode);

  if (!regionCode || legacyRegionCodes.length === 0) {
    return rows;
  }

  const normalizedRegionCode = normalizeCode(regionCode);
  const legacyRegionCodeSet = new Set(legacyRegionCodes.map((value) => normalizeCode(value)));
  let didChange = false;

  const nextRows = rows.map((row) => {
    const rowRegionCode = normalizeCode(row?.regionCode);

    if (!legacyRegionCodeSet.has(rowRegionCode) || rowRegionCode === normalizedRegionCode) {
      return row;
    }

    didChange = true;
    const year = row?.year || "";
    const quarter = row?.quarter || "";
    const districtId = row?.districtId || "";

    return {
      ...row,
      regionCode,
      periodKey: buildAssessmentPeriodKey({ regionCode, year, quarter }),
      submissionKey: buildDistrictSubmissionKey({ regionCode, districtId, year, quarter }),
    };
  });

  if (didChange) {
    writeStoredJson(LOCAL_MONITORING_SUBMISSIONS_STORAGE_KEY, nextRows);
  }

  return nextRows;
}

export function getMonitoringSubmissionForPeriod({ locationNode, regionCode, districtId, year, quarter }) {
  const submissionKey = buildDistrictSubmissionKey({ regionCode, districtId, year, quarter });
  const submissions = locationNode ? getSavedMonitoringSubmissionsForLocation(locationNode) : getSavedMonitoringSubmissions();
  return submissions.find((item) => item?.submissionKey === submissionKey) || null;
}

export function getMonitoringSubmissionsForRegionPeriod({ locationNode, regionCode, year, quarter }) {
  const periodKey = buildAssessmentPeriodKey({ regionCode, year, quarter });
  const submissions = locationNode ? getSavedMonitoringSubmissionsForLocation(locationNode) : getSavedMonitoringSubmissions();

  return submissions.filter((item) => item?.periodKey === periodKey);
}

export function saveMonitoringSubmission(submission) {
  const current = getSavedMonitoringSubmissions();
  const next = [
    submission,
    ...current.filter((item) => item?.submissionKey !== submission?.submissionKey),
  ];

  writeStoredJson(LOCAL_MONITORING_SUBMISSIONS_STORAGE_KEY, next);
  return next;
}

export function buildScopedSections({
  selectedIndicatorIds,
  indicatorGroups,
  subIndicatorRows,
}) {
  const ids = Array.isArray(selectedIndicatorIds) ? selectedIndicatorIds : [];
  const allIndicatorGroups = Array.isArray(indicatorGroups) ? indicatorGroups : [];
  const allSubIndicatorRows = Array.isArray(subIndicatorRows) ? subIndicatorRows : [];

  if (ids.length === 0 || allIndicatorGroups.length === 0) {
    return [];
  }

  const { sectionsByTitle, indicatorsByTitle } = getFallbackSectionMetadataMaps();

  return ids
    .map((indicatorId) => {
      const indicatorGroup = allIndicatorGroups.find(
        (item) => toSafeNumber(item?.indicatorId) === toSafeNumber(indicatorId),
      );

      if (!indicatorGroup) {
        return null;
      }

      const matchedSection =
        sectionsByTitle.get(normalizeText(indicatorGroup.name)) ||
        sectionsByTitle.get(normalizeText(indicatorGroup.code));

      const indicators = allSubIndicatorRows
        .filter(
          (subIndicator) =>
            toSafeNumber(subIndicator?.indicator?.indicatorId || subIndicator?.indicatorId) ===
              toSafeNumber(indicatorId) && subIndicator?.status !== "Inactive",
        )
        .sort((left, right) =>
          sortByCode(left?.subCode, right?.subCode, left?.subName, right?.subName),
        )
        .map((subIndicator, subIndex) => {
          const matchedIndicator =
            indicatorsByTitle.get(normalizeText(subIndicator.subName)) ||
            indicatorsByTitle.get(normalizeText(subIndicator.subCode));

          const maxScore = toSafeNumber(subIndicator.maximumScore) || 0;

          return {
            key: String(subIndicator?.subIndicatorId || subIndicator?.subCode || `${indicatorId}-${subIndex}`),
            code: subIndicator?.subCode || matchedIndicator?.code || `SUB-${subIndex + 1}`,
            title: subIndicator?.subName || matchedIndicator?.title || "Sub-indicator",
            context: matchedIndicator?.context || "",
            note: matchedIndicator?.note || subIndicator?.description || "",
            maxScore,
            meansOfVerification: matchedIndicator?.meansOfVerification || [],
          };
        });

      const totalScore = indicators.reduce((sum, item) => sum + (item.maxScore || 0), 0);

      return {
        key: String(indicatorGroup.indicatorId || indicatorGroup.code || indicatorGroup.name),
        indicatorId: indicatorGroup.indicatorId,
        code: indicatorGroup.code || matchedSection?.code || `IND-${indicatorId}`,
        title: indicatorGroup.name || matchedSection?.title || "Indicator Group",
        description: indicatorGroup.description || matchedSection?.description || "",
        marksLabel: matchedSection?.marksLabel || `${totalScore} marks`,
        icon: matchedSection?.icon || getSectionIcon(indicatorGroup.name),
        indicators,
      };
    })
    .filter(Boolean)
    .sort((left, right) => sortByCode(left?.code, right?.code, left?.title, right?.title));
}
